/* eslint-disable react-hooks/exhaustive-deps */
import { Box } from '@mui/material'
import { lazy, Suspense, useEffect } from 'react'
import { Provider } from 'react-redux'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { PersistGate } from 'redux-persist/integration/react'
import { ErrorBoundary } from './components/ErrorBoundary'
import Loader from './components/Loader'
import RedirectHome from './components/RedirectHome'
import { customerScalling, devCustomerScalling, earthlink, isEntryPoint, isScaling } from './lib/utils'
import PrivateRoute from './Pages/Login/PrivateRoute'
import NotificationMainPage from './Pages/Notifications'
import { persistor, store } from './redux/store'

// Lazy-loaded components
const ConfigPage = lazy(() => import('./Pages/ConfigPage/ConfigPage'))
const CoverageMapPage = lazy(() => import('./Pages/CoverageMap'))
const CustomersDashboard = lazy(() => import('./Pages/Customer/Header'))
const Dashboard = lazy(() => import('./Pages/Dashboard'))
const GroupsMainPage = lazy(() => import('./Pages/Groups'))
const LoginPage = lazy(() => import('./Pages/Login'))
const PrivacyPage = lazy(() => import('./Pages/PrivacyPage/PrivacyPage'))
const DeleteAccount = lazy(() => import('./Pages/PublicPage/DeleteAccount'))
const PageNotFound = lazy(() => import('./Pages/PublicPage/PageNotFound'))
const ReportsPage = lazy(() => import('./Pages/Reports'))
const TermsConditionPage = lazy(() => import('./Pages/TermsConditionPage/TermsConditionPage'))
const AgentsMainPage = lazy(() => import('./Pages/Agents'))
const LegacyDetails = lazy(() => import('./Pages/LegacyDetails'))

function App() {
  useEffect(() => {
    const url = window.location.href
    if (url.endsWith('#')) {
      window.location.href = window.location.origin + window.location.pathname
    }
  }, [])

  const router = createBrowserRouter([
    { path: '/', element: <RedirectHome /> },
    { path: '/delete_account', element: <DeleteAccount /> },
    { path: '/login', element: <LoginPage /> },
    {
      path: '/privacypolicy',
      element: process.env.REACT_APP_REALM === 'entpnt' ? <PrivacyPage /> : <PageNotFound />
    },
    {
      path: '/termscondition',
      element: process.env.REACT_APP_REALM === 'entpnt' ? <TermsConditionPage /> : <PageNotFound />
    },
    {
      path: '/orangeburgtheme.json',
      element: process.env.REACT_APP_REALM === 'entpnt' ? <ConfigPage /> : <PageNotFound />
    },
    {
      element: <PrivateRoute />,
      children: [
        { path: '/dashboard/', element: <Dashboard /> },
        {
          path: '/legacy/',
          element: devCustomerScalling || earthlink ? <Dashboard /> : <PageNotFound />
        },
        {
          path: '/legacy/:id',
          element: devCustomerScalling || earthlink ? <LegacyDetails /> : <PageNotFound />
        },
        { path: '/dashboard/:id', element: <CustomersDashboard /> },
        { path: '/dashboard/:id/:email', element: <CustomersDashboard /> },
        {
          path: '/reports',
          element: process.env.REACT_APP_REALM === 'Scalingtests' ? <ReportsPage /> : <PageNotFound />
        },
        {
          path: '/maps',
          element: process.env.REACT_APP_REALM === 'Scalingtests' ? <CoverageMapPage /> : <PageNotFound />
        },
        {
          path: '/Agent',
          element: customerScalling || isEntryPoint ? <AgentsMainPage /> : <PageNotFound />
        },
        { path: '/groups', element: <GroupsMainPage /> },
        { path: '/groups/:id', element: <GroupsMainPage /> },
        { path: '/batch/:id', element: <GroupsMainPage /> },
        { path: '/notifications', element: isScaling ? <NotificationMainPage /> : <PageNotFound /> }
      ]
    },
    { path: '*', element: <PageNotFound /> }
  ])

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ErrorBoundary>
          <Suspense
            fallback={
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
                <Loader />
              </Box>
            }
          >
            <RouterProvider router={router} />
          </Suspense>
        </ErrorBoundary>
      </PersistGate>
    </Provider>
  )
}

export default App
